import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useEnquiry } from "./EnquiryContext";
import { CONTACT } from "../data/content";

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const useNavScroll = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (id) => {
    if (pathname !== "/") navigate("/", { state: { scrollTo: id } });
    else scrollToId(id);
  };
};

const LINKS = [
  { label: "Destinations", id: "destinations" },
  { label: "Wildlife", id: "wildlife" },
  { label: "Tours", id: "tours" },
  { label: "Adventure", id: "adventure" },
  { label: "Rentals", id: "rentals" },
  { label: "Contact", id: "contact" },
];

const MOBILE_LINKS = [
  { label: "Home", id: "top" },
  { label: "Explore", id: "destinations" },
  { label: "Tours", id: "tours" },
  { label: "Book", id: null },
  { label: "Contact", id: "contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { openEnquiry } = useEnquiry();
  const navScroll = useNavScroll();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) window.__lenis?.stop();
    else window.__lenis?.start();
  }, [menuOpen]);

  const go = (id) => {
    setMenuOpen(false);
    if (id === "top") navigate("/");
    else navScroll(id);
  };

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 inset-x-0 z-[80] transition-all duration-500 ${
          scrolled ? "glass-panel shadow-2xl shadow-black/40" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-[72px]">
          <button
            data-testid="navbar-logo"
            onClick={() => go("top")}
            className="flex items-center gap-2.5 group"
          >
            <span className="w-2 h-2 rotate-45 bg-gold transition-transform duration-500 group-hover:rotate-[135deg]" />
            <span className="font-serif text-lg sm:text-xl tracking-[0.18em] text-cream">
              HIDDENBIOMES
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => navScroll(l.id)}
                className="font-mono text-[11px] uppercase tracking-[0.22em] text-cream/70 hover:text-gold transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              data-testid="nav-call-link"
              className="hidden md:flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] text-cream/70 hover:text-gold transition-colors"
            >
              <Phone size={13} /> {CONTACT.phone}
            </a>
            <button
              data-testid="nav-plan-trip-btn"
              onClick={() => openEnquiry()}
              className="hidden sm:block bg-gold text-forest-950 font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 hover:bg-cream transition-colors duration-300"
            >
              Plan Your Trip
            </button>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMenuOpen(true)}
              className="lg:hidden text-cream p-2"
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[95] bg-forest-950/97 backdrop-blur-xl flex flex-col"
          >
            <div className="flex items-center justify-between px-5 h-[72px]">
              <span className="font-serif text-lg tracking-[0.18em] text-cream flex items-center gap-2.5">
                <span className="w-2 h-2 rotate-45 bg-gold" /> HIDDENBIOMES
              </span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setMenuOpen(false)}
                className="text-cream p-2"
                aria-label="Close menu"
              >
                <X size={24} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {MOBILE_LINKS.map((l, i) => (
                <motion.button
                  key={l.label}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5 }}
                  onClick={() => (l.id ? go(l.id) : (setMenuOpen(false), openEnquiry()))}
                  className="text-left font-serif text-4xl text-cream/85 hover:text-gold transition-colors py-3 border-b border-gold/10 flex items-baseline gap-4"
                >
                  <span className="font-mono text-xs text-gold/60">0{i + 1}</span>
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <div className="px-8 pb-10 font-mono text-xs tracking-[0.15em] text-fog">
              {CONTACT.phone} · {CONTACT.email}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
